import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import UnpublishedOutlinedIcon from '@mui/icons-material/UnpublishedOutlined';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import {
  ACCENT,
  AdminCard,
  AdminEmpty,
  AdminLoading,
  AdminPage,
  AdminPageHeader,
  AdminStatGrid,
  AdminToolbar,
  MetaChip,
  difficultyColor,
  useAdminPageTheme,
} from './adminUi';
import { formatProblemTitle } from '../../common/utils';

// Distinct from the student-facing "selectedLevel"/"selectedSection" keys in
// common/practiceProgress.js — those drive Practice Track resume state and
// must not be cross-contaminated by an admin's filter picks.
const ADMIN_PROBLEMS_LEVEL_KEY = 'adminProblemsSelectedLevel';
const ADMIN_PROBLEMS_SECTION_KEY = 'adminProblemsSelectedSection';

const readStoredFilter = (key) => {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const writeStoredFilter = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // ignore quota / private mode
  }
};

const ManageProblems = () => {
  const loggedInUser = useSelector((state) => state.user.userInfo);
  const navigate = useNavigate();
  const { border, muted, isDark } = useAdminPageTheme();

  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProblemId, setSelectedProblemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [tab, setTab] = useState('unpublished');
  const [selectedLevel, setSelectedLevel] = useState(() => readStoredFilter(ADMIN_PROBLEMS_LEVEL_KEY));
  const [selectedSection, setSelectedSection] = useState(() =>
    readStoredFilter(ADMIN_PROBLEMS_SECTION_KEY),
  );

  const fetchProblems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await proxy.get('/problems/admin');
      setProblems(response.data || []);
    } catch (err) {
      setError('Could not load problems');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProblems();
  }, []);

  const levels = useMemo(
    () => [...new Set(problems.map((p) => p.level).filter(Boolean))].sort(),
    [problems],
  );

  const sections = useMemo(() => {
    const pool = selectedLevel
      ? problems.filter((p) => String(p.level) === String(selectedLevel))
      : problems;
    return [...new Set(pool.map((p) => p.section).filter(Boolean))].sort();
  }, [problems, selectedLevel]);

  const stats = useMemo(() => {
    const live = problems.filter((p) => p.isPublished).length;
    const draft = problems.length - live;
    return { total: problems.length, live, draft };
  }, [problems]);

  const filteredProblems = useMemo(() => {
    return [...problems]
      .filter((problem) => (tab === 'published' ? problem.isPublished : !problem.isPublished))
      .filter((problem) => (selectedLevel ? String(problem.level) === String(selectedLevel) : true))
      .filter((problem) => (selectedSection ? problem.section === selectedSection : true))
      .filter((problem) =>
        problem.title?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }, [problems, tab, selectedLevel, selectedSection, searchQuery]);

  const handleDialogClose = () => {
    setConfirmDialogOpen(false);
    setPublishDialogOpen(false);
    setConfirmDeleteDialogOpen(false);
    setSelectedProblemId(null);
  };

  const handleConfirmUnpublish = async () => {
    try {
      await proxy.put(`/problems/unpublish/${selectedProblemId}`);
      setProblems((prev) =>
        prev.map((problem) =>
          problem._id === selectedProblemId ? { ...problem, isPublished: false } : problem,
        ),
      );
      toast.success('Problem taken down');
    } catch (err) {
      toast.error('Failed to unpublish problem');
      console.error(err);
    } finally {
      handleDialogClose();
    }
  };

  const handleConfirmPublish = async () => {
    try {
      await proxy.put(`/problems/publish/${selectedProblemId}`);
      setProblems((prev) =>
        prev.map((problem) =>
          problem._id === selectedProblemId ? { ...problem, isPublished: true } : problem,
        ),
      );
      toast.success('Problem is live');
    } catch (err) {
      toast.error('Failed to publish problem');
      console.error(err);
    } finally {
      handleDialogClose();
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await proxy.delete(`/problems/${selectedProblemId}`);
      setProblems((prev) => prev.filter((problem) => problem._id !== selectedProblemId));
      toast.success('Problem deleted');
    } catch (err) {
      toast.error('Failed to delete problem');
      console.error(err);
    } finally {
      handleDialogClose();
    }
  };

  const dialogSx = {
    '& .MuiDialog-paper': {
      borderRadius: 2.5,
      border: `1px solid ${border}`,
    },
  };

  return (
    <AdminPage maxWidth={1280}>
      <Helmet>
        <title>Manage Problems | Admin | CrafterHack</title>
      </Helmet>

      <AdminPageHeader
        title="Manage problems"
        subtitle="Publish drafts, edit content, and organize the problem set"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchProblems} sx={{ border: `1px solid ${border}` }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<RateReviewOutlinedIcon />}
              onClick={() => navigate('/admin/problem-feedback')}
              sx={{ textTransform: 'none', fontWeight: 700, borderColor: border }}
            >
              Feedback
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => navigate('/admin/create-problem')}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, boxShadow: 'none' }}
            >
              New problem
            </Button>
          </>
        }
      />

      <AdminStatGrid
        items={[
          { label: 'TOTAL', value: stats.total },
          { label: 'LIVE', value: stats.live, color: '#1a7f37' },
          { label: 'DRAFTS', value: stats.draft, color: '#d97706' },
          { label: 'SHOWING', value: filteredProblems.length },
        ]}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {[
          { id: 'published', label: `Live (${stats.live})` },
          { id: 'unpublished', label: `Drafts (${stats.draft})` },
        ].map((item) => (
          <Button
            key={item.id}
            size="small"
            variant={tab === item.id ? 'contained' : 'outlined'}
            onClick={() => setTab(item.id)}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              bgcolor: tab === item.id ? ACCENT : 'transparent',
              borderColor: tab === item.id ? ACCENT : border,
              boxShadow: 'none',
            }}
          >
            {item.label}
          </Button>
        ))}
      </Box>

      <AdminToolbar>
        <TextField
          size="small"
          placeholder="Search by title…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: muted }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Level</InputLabel>
          <Select
            label="Level"
            value={selectedLevel}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedLevel(value);
              setSelectedSection('');
              writeStoredFilter(ADMIN_PROBLEMS_LEVEL_KEY, value);
              writeStoredFilter(ADMIN_PROBLEMS_SECTION_KEY, '');
            }}
          >
            <MenuItem value="">All levels</MenuItem>
            {levels.map((level) => (
              <MenuItem key={level} value={level}>
                Level {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Section</InputLabel>
          <Select
            label="Section"
            value={selectedSection}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedSection(value);
              writeStoredFilter(ADMIN_PROBLEMS_SECTION_KEY, value);
            }}
          >
            <MenuItem value="">All sections</MenuItem>
            {sections.map((section) => (
              <MenuItem key={section} value={section}>
                {section}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </AdminToolbar>

      {loading ? (
        <AdminLoading />
      ) : error ? (
        <AdminEmpty title="Something went wrong" description={error} />
      ) : filteredProblems.length === 0 ? (
        <AdminEmpty
          title="No problems found"
          description="Try another filter or create a new problem."
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {filteredProblems.map((problem) => {
            const diff = String(problem.difficulty || '').toLowerCase();
            const diffLabel = diff ? diff.charAt(0).toUpperCase() + diff.slice(1) : '—';

            return (
              <AdminCard
                key={problem._id}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                    <MetaChip
                      label={diffLabel}
                      color={difficultyColor(diff)}
                      isDark={isDark}
                    />
                    <MetaChip label={`Level ${problem.level}`} isDark={isDark} />
                    {problem.section && (
                      <MetaChip label={problem.section} color="#2563eb" isDark={isDark} />
                    )}
                    <MetaChip
                      label={problem.isPublished ? 'Live' : 'Draft'}
                      color={problem.isPublished ? '#1a7f37' : '#d97706'}
                      isDark={isDark}
                    />
                  </Box>
                  <Typography
                    component={Link}
                    to={`/admin/problems/${problem.slug}`}
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.98rem',
                      color: 'inherit',
                      textDecoration: 'none',
                      '&:hover': { color: ACCENT },
                    }}
                  >
                    {formatProblemTitle(problem.title)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: muted, fontSize: '0.82rem', mt: 0.35 }}>
                    Order {problem.displayOrder ?? '—'} · {problem.testCases?.length || 0} test cases
                    {loggedInUser?.role === 'admin' && problem.createdBy?.name
                      ? ` · ${problem.createdBy.name}`
                      : ''}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  <Tooltip title="Preview">
                    <IconButton
                      component={Link}
                      to={`/admin/problems/${problem.slug}`}
                      size="small"
                    >
                      <OpenInNewOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/edit-problem/${problem.slug}`)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {loggedInUser?.role === 'admin' &&
                    (tab === 'unpublished' ? (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PublishOutlinedIcon />}
                          onClick={() => {
                            setSelectedProblemId(problem._id);
                            setPublishDialogOpen(true);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 800,
                            bgcolor: '#1a7f37',
                            boxShadow: 'none',
                          }}
                        >
                          Go live
                        </Button>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedProblemId(problem._id);
                              setConfirmDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UnpublishedOutlinedIcon />}
                        onClick={() => {
                          setSelectedProblemId(problem._id);
                          setConfirmDialogOpen(true);
                        }}
                        sx={{ textTransform: 'none', fontWeight: 700, borderColor: border }}
                      >
                        Take down
                      </Button>
                    ))}
                </Box>
              </AdminCard>
            );
          })}
        </Box>
      )}

      <Dialog open={confirmDeleteDialogOpen} onClose={handleDialogClose} sx={dialogSx}>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete problem?</DialogTitle>
        <DialogContent>
          <DialogContentText>This cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={handleDialogClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialogOpen} onClose={handleDialogClose} sx={dialogSx}>
        <DialogTitle sx={{ fontWeight: 800 }}>Take problem down?</DialogTitle>
        <DialogContent>
          <DialogContentText>It will be hidden from learners until published again.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={handleDialogClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUnpublish}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, boxShadow: 'none' }}
          >
            Take down
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={publishDialogOpen} onClose={handleDialogClose} sx={dialogSx}>
        <DialogTitle sx={{ fontWeight: 800 }}>Publish problem?</DialogTitle>
        <DialogContent>
          <DialogContentText>This will make the problem visible to all users.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={handleDialogClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPublish}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#1a7f37', boxShadow: 'none' }}
          >
            Go live
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPage>
  );
};

export default ManageProblems;
